/**
 * Test Run Constants
 * Contains all constants related to test runs
 */

// Jenkins build polling
export const BUILD_POLL_INITIAL_MS = 10000;
export const BUILD_POLL_MAX_INTERVAL_MS = 120000;
export const BUILD_POLL_MAX_COUNT = 10;
// How often to re-check build details once full_build_url is known (until inProgress=false)
export const BUILD_DETAILS_POLL_INTERVAL_MS = 60000;

import { STATUS_COLORS } from '@/constants/statusColors'

// Test run environments
export const TEST_RUN_ENVIRONMENTS = [
  { value: 'development', label: 'Development' },
  { value: 'versioned_staging', label: 'Versioned Staging' },
  { value: 'default_staging', label: 'Default Staging' },
  { value: 'interop', label: 'Interop' },
  { value: 'beta', label: 'Beta' },
  { value: 'production', label: 'Production' }
]

/** Client platform under test (R2) — values must match backend TEST_RUN_PLATFORMS */
export const TEST_RUN_PLATFORMS = [
  { value: 'web', label: 'Web' },
  { value: 'ios', label: 'iOS' },
  { value: 'android', label: 'Android' },
  { value: 'api', label: 'API' },
]

// Test run status
export const TEST_RUN_STATUS = [
  { value: 'YET_TO_START', label: 'Yet to Start', color: STATUS_COLORS.NOT_STARTED.badge },
  { value: 'IN_PROGRESS', label: 'In Progress', color: STATUS_COLORS.IN_PROGRESS.badge },
  { value: 'PAUSED', label: 'Paused', color: STATUS_COLORS.PAUSED.badge },
  { value: 'COMPLETED', label: 'Completed', color: STATUS_COLORS.COMPLETED.badge },
  { value: 'CANCELLED', label: 'Cancelled', color: STATUS_COLORS.CANCELLED.badge }
]

// Test run status values (auto-generated from TEST_RUN_STATUS array)
export const TEST_RUN_STATUS_VALUES = Object.fromEntries(
  TEST_RUN_STATUS.map(status => [
    status.value.toUpperCase().replace(/([a-z])([A-Z])/g, '$1_$2'),
    status.value
  ])
)


// Status options for dropdowns and filters (generated from TEST_RUN_STATUS)
export const TEST_RUN_STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  ...TEST_RUN_STATUS.map(status => ({ value: status.value, label: status.label }))
]

// Date range filter options
export const DATE_RANGE_OPTIONS = [
  { value: '', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' }
]

// ETA range filter options
export const ETA_RANGE_OPTIONS = [
  { value: '', label: 'All ETA' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'today', label: 'Due Today' },
  { value: 'week', label: 'Due This Week' },
  { value: 'month', label: 'Due This Month' },
  { value: 'not_set', label: 'Not Set' }
]

// Helper functions to get options by value
export const getTestRunEnvironmentByValue = (value) => {
  return TEST_RUN_ENVIRONMENTS.find(env => env.value === value)
}

export const getTestRunPlatformByValue = (value) => {
  return TEST_RUN_PLATFORMS.find(platform => platform.value === value)
}

export const getTestRunStatusByValue = (value) => {
  return TEST_RUN_STATUS.find(status => status.value === value)
}

// Badge color classes for status display
export const TEST_RUN_STATUS_BADGE_COLORS = {
  'YET_TO_START': 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  'IN_PROGRESS': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  'PAUSED': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  'COMPLETED': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  'CANCELLED': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
}

// Status variant mappings for Badge component
export const TEST_RUN_STATUS_VARIANTS = {
  'YET_TO_START': 'secondary',
  'IN_PROGRESS': 'warning',
  'PAUSED': 'warning',
  'COMPLETED': 'success',
  'CANCELLED': 'danger'
}

// Color to variant mapping
export const COLOR_TO_VARIANT_MAP = {
  'bg-gray-100': 'secondary',
  'bg-blue-100': 'warning', 
  'bg-yellow-100': 'warning',
  'bg-green-100': 'success',
  'bg-red-100': 'danger'
}

// Helper functions to get color classes
export const getTestRunStatusColor = (status) => {
  const statusConfig = getTestRunStatusByValue(status)
  return statusConfig?.color || 'bg-gray-100 text-gray-800'
}

// Helper function to get status information (label, variant, color)
export const getTestRunStatusInfo = (status) => {
  // Normalize status to uppercase for comparison
  const normalizedStatus = status?.toUpperCase()
  
  // Map old status values to new ones for backward compatibility
  const statusMapping = {
    'PLANNED': 'YET_TO_START'
  }
  
  // Use mapped status if available, otherwise use the original
  const mappedStatus = statusMapping[normalizedStatus] || normalizedStatus
  
  // Find the status in TEST_RUN_STATUS array
  const statusObj = TEST_RUN_STATUS.find(s => s.value === mappedStatus)
  
  if (statusObj) {
    // Extract base color class from full color string (e.g., 'bg-green-100' from 'bg-green-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200')
    const baseColorClass = statusObj.color.split(' ')[0]
    
    return {
      label: statusObj.label,
      variant: COLOR_TO_VARIANT_MAP[baseColorClass] || 'secondary',
      color: statusObj.color,
      badgeColor: TEST_RUN_STATUS_BADGE_COLORS[mappedStatus] || TEST_RUN_STATUS_BADGE_COLORS['YET_TO_START']
    }
  }
  
  // Fallback for unknown statuses
  return {
    label: status || 'Unknown',
    variant: 'secondary',
    color: 'bg-gray-100 text-gray-800',
    badgeColor: TEST_RUN_STATUS_BADGE_COLORS['YET_TO_START']
  }
}
